import animation, { fadeIn, fadeInUp } from '@helpers/animation';
import brand from '@helpers/brand';
import { cssVar } from '@helpers/cssVar';
import fonts from '@helpers/fonts';
import { Device, from, until } from '@helpers/media';
import svgBg from '@img/general/info-card-bg.svg';
import svgBgViewportLight from '@img/general/info-card-viewport-light-bg.svg';
import { SitePadding, SiteWidth } from '@stories/Components/Global/SiteWide/SiteWide.styles';
import SectionWrapper from '@stories/Components/SectionWrapper/SectionWrapper';
import TitleBlockStyles from '@stories/Components/TitleBlock/TitleBlock.styles';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';
import RichTextStyles from '../RichText/RichText.styles';
/* prettier-ignore */
const ImageViewportWidth = `calc(100% + ((100vw - ${cssVar('siteWidth')} - (2 * ${cssVar('sitePadding')})) / 2) + ${cssVar('sitePadding')})`;
const Container = styled(SectionWrapper) `
  background: ${cssVar('bgColor')};

  ${TitleBlockStyles.Container} {
    max-width: 311px;
  }

  &[data-size='narrow'] {
    padding-top: ${cssVar('sectionMargin')};
    padding-bottom: ${cssVar('sectionMargin')};
  }

  &[data-size='viewport'] {
    overflow: hidden;
  }

  @media ${from(Device.TabletLarge)} {
    ${() => Logo}, ${() => Title}, ${() => Content}, ${() => CtaWrapper} {
      opacity: 0;
      transform: translateY(40px);
    }

    ${() => Image} {
      opacity: 0;
    }

    ${({ isActive }) => isActive &&
    css `
        ${() => Logo} {
          ${fadeInUp(isActive, 40, 375, 0, animation.easeInOutCubic)};
        }

        ${() => Title} {
          ${fadeInUp(isActive, 40, 375, 150, animation.easeInOutCubic)};
        }

        ${() => Content} {
          ${fadeInUp(isActive, 40, 375, 300, animation.easeInOutCubic)};
        }

        ${() => CtaWrapper} {
          ${fadeInUp(isActive, 40, 375, 450, animation.easeInOutCubic)};
        }

        ${() => Image} {
          ${fadeIn(isActive, 375, 200, animation.easeInOutCubic)};
        }
      `};
  }
`;
const Inner = styled.div `
  display: grid;
  grid-template-columns: 1fr;

  @media ${from(Device.Tablet)} {
    grid-template-columns: 1fr 1fr;
  }

  [data-size='narrow'] & {
    background: ${cssVar('bgColor')};
    max-width: 1360px;
    margin: 0 auto;
  }

  [data-size='narrow'][data-theme='light'] & {
    filter: drop-shadow(0px 3px 12px ${rgba(brand.brown, 0.18)});
  }

  [data-size='viewport'] & {
    display: grid;
    gap: 0;
    grid-template-columns: 1fr;

    @media ${from(Device.TabletLarge)} {
      grid-template-columns: 1fr 1fr;
    }
  }

  [data-size='wide'] & {
    gap: 24px;

    @media ${from(Device.Tablet)} {
      gap: 0;
    }
  }
`;
const ContentColumn = styled.div `
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media ${until(Device.Tablet)} {
    grid-row: 2;
  }

  @media ${from(Device.Desktop)} {
    padding: 72px 0;
  }

  [data-layout='imageLeft'] & {
    @media ${from(Device.Tablet)} {
      grid-column: 2;
      grid-row: 1;
    }
  }

  [data-size='narrow'] & {
    padding: 28px 20px;

    @media ${from(Device.Tablet)} {
      padding: 28px 64px;
    }

    @media ${from(Device.Desktop)} {
      padding: 72px 71px;
    }
  }

  [data-size='narrow'][data-theme='light'] & {
    background-image: url('${svgBg}');
    background-position: center left;
    background-size: cover;
  }

  [data-size='viewport'] & {
    position: relative;
    max-width: none;

    @media ${until(Device.TabletLarge)} {
      grid-column: 1 !important;
      grid-row: 2 !important;
      margin-left: calc(${cssVar('sitePadding')} * -1);
      margin-right: calc(${cssVar('sitePadding')} * -1);
      padding: 24px ${cssVar('sitePadding')};
    }

    &::before {
      content: '';
      position: absolute;
      top: 0;
      display: block;
      background: ${cssVar('bgColor')};
      height: 100%;
      width: 100%;

      @media ${from(Device.TabletLarge)} {
        max-width: none;
        width: calc(100% + var(--sitePadding));
      }

      @media ${from(SiteWidth + SitePadding * 2)} {
        width: ${ImageViewportWidth};
      }
    }
  }

  [data-size='viewport'][data-theme='light'] &::before {
    background-image: url('${svgBgViewportLight}');
    background-position: center left;
    background-repeat: no-repeat;
    background-size: cover;
  }

  [data-size='viewport'][data-layout='imageLeft'] & {
    @media ${from(Device.TabletLarge)} {
      padding-left: 64px;
      padding-right: 0px;
    }

    @media ${from(Device.Desktop)} {
      padding-left: 128px;
      padding-right: 0px;
    }

    &::before {
      right: auto;
      left: 0;
    }
  }

  [data-size='viewport'][data-layout='imageRight'] & {
    @media ${from(Device.TabletLarge)} {
      padding-left: 0px;
      padding-right: 64px;
    }

    @media ${from(Device.Desktop)} {
      padding-left: 0px;
      padding-right: 128px;
    }

    &::before {
      right: 0;
      left: auto;
    }
  }

  [data-size='wide'][data-layout='imageLeft'] & {
    max-width: 666px;
    padding-right: 0;

    @media ${from(Device.Tablet)} {
      padding-left: 64px;
    }

    @media ${from(Device.Desktop)} {
      padding-left: 128px;
    }
  }

  [data-size='wide'][data-layout='imageRight'] & {
    max-width: 666px;
    padding-left: 0;

    @media ${from(Device.Tablet)} {
      padding-right: 64px;
    }
  }
`;
const ImageColumn = styled.div `
  @media ${until(Device.Tablet)} {
    grid-row: 1;
  }

  [data-layout='imageLeft'] & {
    @media ${from(Device.Tablet)} {
      grid-column: 1;
      grid-row: 1;
    }
  }

  [data-size='viewport'] & {
    position: relative;
    min-height: 734px;

    @media ${until(Device.TabletLarge)} {
      grid-column: 1 !important;
      grid-row: 1 !important;
      margin-left: calc(${cssVar('sitePadding')} * -1);
      margin-right: calc(${cssVar('sitePadding')} * -1);
      height: 0;
      min-height: 0;
      padding-bottom: ${(287 / 375) * 100}%;
    }
  }
`;
const ContentWrapper = styled.div `
  position: relative;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
`;
const Logo = styled.img `
  display: inline-block;
  object-fit: contain;
  object-position: center left;
  margin: 0 0 24px;
  max-height: 50px;

  @media ${from(Device.TabletLarge)} {
    max-height: 65px;
  }
`;
const Title = styled.h2 `
  ${fonts.gillSans.semiBold};
  font-size: 28px;
  line-height: 36px;
  margin: 0 0 16px;
  text-transform: uppercase;

  @media ${from(Device.Tablet)} {
    font-size: 35px;
    line-height: 47px;
    margin: 0 0 16px;
  }

  [data-size='narrow'] & {
    margin-bottom: 22px;
  }
`;
const Subtitle = styled.small `
  display: block;
  ${fonts.gillSans.regular};
  font-size: 16px;
  line-height: 21px;
  margin: 0 0 4px;
  text-transform: uppercase;
`;
const Content = styled.div `
  ${RichTextStyles.contentStyles};
  margin: 0 0 16px;

  @media ${from(Device.Tablet)} {
    margin: 0 0 38px;
  }

  [data-size='narrow'] & {
    margin-bottom: 32px;
  }
`;
const CtaWrapper = styled.div ``;
const Image = styled.img `
  display: block;
  height: 100%;
  object-fit: cover;
  object-position: center center;
  width: 100%;

  [data-size='viewport'] & {
    position: absolute;
    height: 100%;
    max-width: none;
    width: 100%;

    @media ${from(Device.TabletLarge)} {
      max-width: none;
      width: calc(100% + var(--sitePadding));
      opacity: 1 !important;
    }

    @media ${from(SiteWidth + SitePadding * 2)} {
      width: ${ImageViewportWidth};
    }
  }

  [data-size='viewport'][data-layout='imageLeft'] & {
    right: 0;
    left: auto;
  }

  [data-size='viewport'][data-layout='imageRight'] & {
    right: auto;
    left: 0;
  }
`;
export default {
    Container,
    Content,
    ContentColumn,
    ContentWrapper,
    CtaWrapper,
    Image,
    ImageColumn,
    Inner,
    Logo,
    Subtitle,
    Title,
};
